// Tide & current station markers — the SPEC OF RECORD since 2026-09-04.
//
// Randy ruled the design board the day he saw it: "i love B & L2"
// (2026-09-04). This file is option B's drawing, ported verbatim from that
// board into the furniture set's grammar; the label half (L2) is a style
// contract and lives in docs/frontend-guide.md, "Tide & current station
// markers — PRODUCTION spec (B + L2, 2026-09-04)".
//
// WHY B — shape is the class.
//   Tide keeps the station disc; current moves into a ROUNDED DIAMOND, which
//   is the chart's own tidal-diamond convention — literally where a mariner
//   already expects to read a current. Until now both classes were the same
//   solid blue disc and the class was carried only by the glyph inside it, so
//   at map size a 2 px white gauge line and a 2 px white arrow were the same
//   amount of white. With B the silhouette answers "tide or current" before
//   the glyph is resolved, which is what makes the set GREYSCALE-SEPARABLE:
//   circle vs diamond survives colour-blind vision, a printed screenshot, and
//   a disc sitting on water almost exactly its own value. It also gives slack
//   a real identity — a bare diamond still says "current station", where
//   today's bare disc says nothing (or "missing glyph").
//   Cost, stated: a second silhouette in a family whose first rule is
//   silhouette = class. That is exactly why it must be the chart's diamond
//   and not an invented shape.
//
// THE RESERVED BLUE. #4a8bc8 is the station colour and is reserved for
//   stations across the whole furniture family (tools/furniture-icons.js
//   never paints a disc in it except `buoy-mooring`'s band). The tide's
//   WATER is the deeper #2c6394, because "darker blue = more water" is the
//   one thing the depth ramp already teaches every user of this map: the
//   disc fills to the tide's fraction of range, so level reads as an AREA,
//   not a tick you have to find. White ink reads on both tones, so the glyph
//   never changes colour with the level.
//
// SIZES. Authored in the family's 36-unit space; STATION.SIZE = 29 nominal px
//   is the drawing scale, which puts the r=12.5 disc at 20 px on screen at
//   icon-size 1.0 (≈22 px point-to-point for the diamond, casing included).
//   One step above the POI disc's 17 px and above today's 18 px: stations
//   outrank both, and size is how the family says so.
//
// pixelRatio 2. The canvas is 2x backing and registers with
//   `{ pixelRatio: 2 }`, like every other icon in this repo. Today's shipping
//   station markers are a 1x 24 px bitmap with no pixelRatio at all —
//   resampled by the browser on every retina device. That is a DEFECT this
//   spec fixes, not a style choice.
//
// SELECTED. The family already has a word for "this one is emitting
//   something": light glows. Selection borrows the grammar, not the colour —
//   a soft navy→station-blue radial glow (r 11.5 → 18) rendered BEHIND the
//   ink, no ring, no rays, nothing added on top of the mark. It cannot be
//   confused with a lit navaid (that glow is warm gold and carries rays), and
//   it survives at true size where a 1 px selection ring would just thicken
//   the casing. Selected marks are SPRITE TWINS (`<name>-selected`), the same
//   way `-lit` twins work in the furniture set: the glow is a gradient, so it
//   cannot be expressed in style paint.
//
// NO INK-TRIM, deliberately (the furniture set trims each canvas to its own
//   ink; this set does not). A marker's collision box is its canvas, and a
//   station's canvas must not change as its DATA changes: a trimmed slack
//   diamond would reserve a different box than a flooding one, and a tide
//   station's box would breathe with the tide. Stations keep the full 36-box
//   so one station is one box at every state — and the selected twins, whose
//   glow reaches the box edge, keep it for free.
//
// PORTING. Dependency-free and 2D-context-only by construction: no
//   `roundRect` (the diamond uses the manual quadratic path), no DOM outside
//   `renderAll`, no library. Canvas here and on web, Skia on RN — the port
//   path stationMarkerIcons.js proved. Native (TidesNearMe82) STAYS AS-IS by
//   Randy's decision, 2026-09-04: web first, native follows from the guide,
//   never from memory.

// ---- the authored constants ------------------------------------------------
const STATION = {
  BLUE: "#4a8bc8",   // reserved station blue — identity colour, both classes
  DEEP: "#2c6394",   // the water inside a tide disc (darker = more water)
  WHITE: "#ffffff",  // casing and glyph ink
  SIZE: 29,          // nominal drawing px; 36-unit space maps onto it
  R_DISC: 12.5,      // tide disc radius, authored units → 20 px at icon-size 1
  DIA_H: 9.8,        // current diamond: half-side of the rounded square
  DIA_R: 3.4,        //   ... and its corner radius
  WL_TOP: 9,         // the gauge window inside the disc: full tide
  WL_BOT: 27,        //   ... and empty
  LEVELS: 8,         // quantized tide steps in the sprite set (0..7)
  BUCKETS: 32,       // quantized current directions (0..31, 11.25° apart)
};

// ---- primitives (ported verbatim from the approved board) ------------------

function stationDiscPath(r) {
  return (x) => { x.beginPath(); x.arc(18, 18, r, 0, Math.PI * 2); };
}

// A rounded square rotated 45° = the chart's tidal diamond. Written with the
// manual quadratic path on purpose: `roundRect` is absent in older canvas
// implementations and in the Skia port, and this set must draw identically in
// all three.
function stationDiamondPath(h, rad) {
  return (x) => {
    x.save();
    x.translate(18, 18);
    x.rotate(Math.PI / 4);
    x.beginPath();
    x.moveTo(-h + rad, -h);
    x.lineTo(h - rad, -h); x.quadraticCurveTo(h, -h, h, -h + rad);
    x.lineTo(h, h - rad); x.quadraticCurveTo(h, h, h - rad, h);
    x.lineTo(-h + rad, h); x.quadraticCurveTo(-h, h, -h, h - rad);
    x.lineTo(-h, -h + rad); x.quadraticCurveTo(-h, -h, -h + rad, -h);
    x.closePath();
    x.restore();
  };
}

const STATION_DISC = stationDiscPath(STATION.R_DISC);
const STATION_DIAMOND = stationDiamondPath(STATION.DIA_H, STATION.DIA_R);

// The family's B-finish contact shadow: the mark sits ON the water.
function stationShadowed(x, path, fill) {
  x.save();
  x.shadowColor = "rgba(10,30,50,.3)";
  x.shadowBlur = 2.5;
  x.shadowOffsetY = 1.2;
  path(x);
  x.fillStyle = fill;
  x.fill();
  x.restore();
}

function stationCasing(x, path, col, w) {
  path(x);
  x.strokeStyle = col;
  x.lineWidth = w;
  x.stroke();
}

// Authored y of the waterline for a 0..1 fraction of the station's range.
function stationWaterline(frac) {
  return STATION.WL_BOT - frac * (STATION.WL_BOT - STATION.WL_TOP);
}

// The tide's water: the deeper blue, clipped to the silhouette, with a bright
// surface line on top of it.
function stationWaterFill(x, path, frac, col) {
  if (frac <= 0.001) return;
  const wl = stationWaterline(frac);
  x.save();
  path(x);
  x.clip();
  x.fillStyle = col;
  x.fillRect(2, wl, 32, 34 - wl);
  x.strokeStyle = "rgba(255,255,255,.85)";
  x.lineWidth = 1.1;
  x.beginPath();
  x.moveTo(3, wl); x.lineTo(33, wl);
  x.stroke();
  x.restore();
}

// Rising / falling: a chevron at the top / bottom of the gauge window.
function stationChevron(x, y, dir, col) {
  const s = 3.4;
  x.strokeStyle = col;
  x.lineWidth = 2.1;
  x.beginPath();
  if (dir === "r") {
    x.moveTo(18 - s, y + s * 0.82); x.lineTo(18, y); x.lineTo(18 + s, y + s * 0.82);
  } else {
    x.moveTo(18 - s, y - s * 0.82); x.lineTo(18, y); x.lineTo(18 + s, y - s * 0.82);
  }
  x.stroke();
}

// High / low: the tide is not going anywhere, so the mark is a FLAT tick.
function stationFlatTick(x, y, col) {
  x.strokeStyle = col;
  x.lineWidth = 2.2;
  x.beginPath();
  x.moveTo(18 - 4.4, y); x.lineTo(18 + 4.4, y);
  x.stroke();
}

// The current arrow: constant length, rotated to the set direction. Length 13
// inside the diamond (the disc variant on the board used 15; the diamond has
// less room across its flats).
function stationArrow(x, deg, col, len, lw) {
  x.save();
  x.translate(18, 18);
  x.rotate((deg * Math.PI) / 180);
  const h = len / 2, b = len * 0.30;
  x.strokeStyle = col;
  x.lineWidth = lw;
  x.lineCap = "round";
  x.lineJoin = "round";
  x.beginPath();
  x.moveTo(0, h); x.lineTo(0, -h);
  x.moveTo(-b, -h + b); x.lineTo(0, -h); x.lineTo(b, -h + b);
  x.stroke();
  x.restore();
}

// Slack: a short bar. Not "nothing" — nothing is what a missing glyph looks
// like, which is the bug in today's bare disc.
function stationBar(x, col, w, lw) {
  x.strokeStyle = col;
  x.lineWidth = lw;
  x.beginPath();
  x.moveTo(18 - w / 2, 18); x.lineTo(18 + w / 2, 18);
  x.stroke();
}

// Selection glow. The halo is only visible between the casing (r 13.5) and the
// box edge, so the gradient starts UNDER the casing and does all its work out
// there. Navy at the core so it reads against the pale shallow-water fills.
function stationSelectionGlow(x) {
  const g = x.createRadialGradient(18, 18, 11.5, 18, 18, 18);
  g.addColorStop(0, "rgba(20,58,96,.92)");
  g.addColorStop(0.34, "rgba(30,74,116,.66)");
  g.addColorStop(0.68, "rgba(44,99,148,.30)");
  g.addColorStop(1, "rgba(74,139,200,0)");
  x.fillStyle = g;
  x.beginPath();
  x.arc(18, 18, 18, 0, Math.PI * 2);
  x.fill();
}

// ---- the two drawing functions --------------------------------------------

/**
 * Tide station — the filled station disc.
 *
 * @param ctx    a 2D context already scaled so the 36-unit space fills it
 *               (renderAll does this; a caller drawing by hand must).
 * @param level  0..1, the tide's fraction of this station's range. Ignored for
 *               trend "high" (drawn full) and "low" (drawn empty), which is
 *               what the `tide-marker-high` / `-low` sprites are.
 * @param trend  "rising" | "falling" | "high" | "low"
 * @param selected  draw the selection glow behind the ink.
 */
function drawTideMarker(ctx, opts) {
  const o = opts || {};
  const trend = o.trend || "rising";
  let frac = typeof o.level === "number" ? o.level : 0;
  if (trend === "high") frac = 1;
  if (trend === "low") frac = 0;
  frac = Math.max(0, Math.min(1, frac));
  const wl = stationWaterline(frac);
  if (o.selected) stationSelectionGlow(ctx);
  stationShadowed(ctx, STATION_DISC, STATION.BLUE);
  stationWaterFill(ctx, STATION_DISC, frac, STATION.DEEP);
  stationCasing(ctx, STATION_DISC, STATION.WHITE, 2);
  if (trend === "rising") stationChevron(ctx, 10.6, "r", STATION.WHITE);
  else if (trend === "falling") stationChevron(ctx, 25.4, "f", STATION.WHITE);
  else if (trend === "high") stationFlatTick(ctx, wl + 3.2, STATION.WHITE);
  else if (trend === "low") stationFlatTick(ctx, wl - 3.2, STATION.WHITE);
}

/**
 * Current station — the rounded tidal diamond.
 *
 * @param ctx     as above.
 * @param bucket  0..31, the direction the current SETS TOWARD, clockwise from
 *                true north: bucket * 11.25°. Or the string "slack".
 * @param selected  draw the selection glow behind the ink.
 */
function drawCurrentMarker(ctx, opts) {
  const o = opts || {};
  if (o.selected) stationSelectionGlow(ctx);
  stationShadowed(ctx, STATION_DIAMOND, STATION.BLUE);
  stationCasing(ctx, STATION_DIAMOND, STATION.WHITE, 2);
  if (o.bucket === "slack" || o.bucket === null || o.bucket === undefined) {
    stationBar(ctx, STATION.WHITE, 9, 2.4);
    return;
  }
  const b = ((Math.round(Number(o.bucket)) % STATION.BUCKETS) + STATION.BUCKETS) % STATION.BUCKETS;
  stationArrow(ctx, b * (360 / STATION.BUCKETS), STATION.WHITE, 13, 2.4);
}

// ---- the image-name table --------------------------------------------------
// TIDE NAMES ARE TODAY'S SERVER NAMES, UNCHANGED — the tide silhouette did not
// change, so `tide-marker-r-4` keeps meaning what it means and ExploreMap.jsx
// keeps working without an expression edit (TideMap.jsx read the same names
// until station pages moved onto ExploreMap and it was retired). CURRENT NAMES ARE
// NEW (`current-marker-*`, replacing today's `current-marker-arrow-*`-style
// disc set) because the SHAPE changed: a consumer that selects sprites by name
// must be updated DELIBERATELY rather than silently inheriting a diamond where
// it drew a disc. A missing image is a loud console warning; a silently swapped
// silhouette is not.

function stationIconSpecs() {
  const specs = [];
  for (const [key, trend] of [["r", "rising"], ["f", "falling"]]) {
    for (let i = 0; i < STATION.LEVELS; i++) {
      specs.push([`tide-marker-${key}-${i}`, "tide", { level: i / (STATION.LEVELS - 1), trend }]);
    }
  }
  specs.push(["tide-marker-high", "tide", { trend: "high" }]);
  specs.push(["tide-marker-low", "tide", { trend: "low" }]);
  for (let b = 0; b < STATION.BUCKETS; b++) {
    specs.push([`current-marker-${b}`, "current", { bucket: b }]);
  }
  specs.push(["current-marker-slack", "current", { bucket: "slack" }]);
  return specs;
}

const STATION_ICON_NAMES = stationIconSpecs().map(([name]) => name);

// ---- rendering -------------------------------------------------------------
// One canvas per icon at `pixelRatio` backing, the 36-unit space scaled onto
// STATION.SIZE nominal px — the same K-is-invariant discipline as
// furniture-icons.js: the scale is a function of SIZE and the ratio only, never
// of the icon.

function renderStationIcon(draw, opts, pixelRatio) {
  const R = pixelRatio || 2;
  const box = Math.round(STATION.SIZE * R);
  const c = document.createElement("canvas");
  c.width = box; c.height = box;
  const x = c.getContext("2d");
  x.scale(box / 36, box / 36);   // authored 36-unit space
  x.lineJoin = "round";
  x.lineCap = "round";
  draw(x, opts);
  return x.getImageData(0, 0, box, box);
}

/**
 * Every station icon as ImageData, keyed by image name, ready for
 * `map.addImage(name, img, { pixelRatio })`. Each icon also gets a
 * `<name>-selected` twin carrying the selection glow.
 */
function renderAll(pixelRatio = 2) {
  const out = {};
  for (const [name, kind, opts] of stationIconSpecs()) {
    const draw = kind === "tide" ? drawTideMarker : drawCurrentMarker;
    out[name] = renderStationIcon(draw, opts, pixelRatio);
    out[`${name}-selected`] = renderStationIcon(
      draw, Object.assign({}, opts, { selected: true }), pixelRatio);
  }
  return out;
}

/** Register the whole set on a MapLibre map, pixelRatio 2. */
function registerStationIcons(map, pixelRatio = 2) {
  const icons = renderAll(pixelRatio);
  for (const [name, img] of Object.entries(icons)) {
    if (!map.hasImage(name)) map.addImage(name, img, { pixelRatio });
  }
  return Object.keys(icons).length;
}

// ---- L2 labels -------------------------------------------------------------
// The label contract lives in docs/frontend-guide.md; these helpers are the
// executable form of it, and they take NO new data: the state word is the same
// value the marker code already uses to pick the sprite.
//   tides    "Rising 5.1 ft" / "Falling 2.3 ft" / "High 6.0 ft" / "Low 0.4 ft"
//   currents "Flood 1.4 kt" / "Ebb 2.1 kt" / "Slack"
// Below z10 the value alone ("5.1 ft"): markers are dots down there and they
// collide hard, so a longer string just loses the collision and shows nothing.

const TIDE_STATE_WORD = { rising: "Rising", falling: "Falling", high: "High", low: "Low" };
const CURRENT_STATE_WORD = { flood: "Flood", ebb: "Ebb", slack: "Slack" };

function tideLabel(trend, valueFt, zoom) {
  const v = `${Number(valueFt).toFixed(1)} ft`;
  if (zoom !== undefined && zoom < 10) return v;
  const w = TIDE_STATE_WORD[trend];
  return w ? `${w} ${v}` : v;
}

function currentLabel(flow, valueKt, zoom) {
  if (flow === "slack") return CURRENT_STATE_WORD.slack;
  const v = `${Number(valueKt).toFixed(1)} kt`;
  if (zoom !== undefined && zoom < 10) return v;
  const w = CURRENT_STATE_WORD[flow];
  return w ? `${w} ${v}` : v;
}

// ---- how this file is consumed --------------------------------------------
// A CLASSIC SCRIPT, like tools/furniture-icons.js: `<script src>` in the
// viewer, vendored/inlined by the web client, ported by hand to Skia on RN.
// No `export` keyword — that would be a syntax error in a classic script —
// so the API is published on `globalThis.StationIcons` (and on
// `module.exports` where a CommonJS loader is what evaluated it). Tests and
// node tooling read the global after importing the file for its side effects.
const StationIcons = {
  STATION, STATION_ICON_NAMES, stationIconSpecs,
  drawTideMarker, drawCurrentMarker,
  renderAll, registerStationIcons,
  tideLabel, currentLabel,
};

if (typeof globalThis !== "undefined") globalThis.StationIcons = StationIcons;
if (typeof module !== "undefined" && module.exports) module.exports = StationIcons;

// ---- Vendoring note (the ONLY edit to this file) ----
//
// Everything above this line is byte-identical to
// ../marine-map-layers/tools/station-icons.js, which is the spec of record for
// the station markers. Vendored VERBATIM for the same reason the furniture set
// is: the drawing IS the design decision, and a hand-copy diverges silently.
//
// The reference loads it with a plain <script> tag, so its declarations are
// globals there and it publishes `globalThis.StationIcons`. Vite has no
// globals, so the module needs an export list. That is the whole diff: no
// declaration above is renamed, reordered, or touched, and the file is
// .prettierignore'd so the next re-sync diffs against the spec rather than
// against Prettier's opinions. Re-sync by copying the file again and
// re-appending this block.
export {
  STATION,
  STATION_ICON_NAMES,
  stationIconSpecs,
  drawTideMarker,
  drawCurrentMarker,
  renderAll,
  registerStationIcons,
  tideLabel,
  currentLabel,
}
